/**
 * SOLID Review
 * S — Strong. One concern: the auth funnel and the profile as data — what React asks for, what the auth
 *     server answers, and what the decisions answer the Application.
 * O — Good. A new auth step is one command, one answer and one DTO here.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Only the auth moves; the session's entry and seating are SessionMoves'.
 * D — Good. zod and the shared primitives only.
 */
import { z } from 'zod';
import { Identifier } from './primitives';

/**
 * SIGNING IN, SIGNING UP, THE PROFILE (frontend strict-ports plan, FE3, 2026-10-03). The auth screens
 * called the REST adapter and wrote the auth store themselves; the rules they applied on the way — the
 * address must be one, the code four digits, the nickname 1–32 characters, the consent ticked, "no account"
 * means sign up in place — are decisions now, and these are their shapes.
 *
 * THE TOKEN IS NOT HERE. A signed-in identity reaches the Domain as its player id, name and credits; the
 * JWT that came with it stays inside Infrastructure's `ClientAuthServer`, which keeps it.
 */

// ── what the auth server answers ──────────────────────────────────────────────────────────────

/** The account a stored token belongs to: its player id, read from a token that is still valid. */
export const PayloadTokenAccount = z.object({ playerId: Identifier }).meta({ id: 'PayloadTokenAccount' });

/** A code accepted: who signed in (the server's `AuthSuccessResponse`, without its token). */
export const PayloadSignedIn = z.object({
  playerId: Identifier,
  displayName: z.string(),
  matchCredits: z.number().int(),
}).meta({ id: 'PayloadSignedIn' });

/** The account as `me` answers it, for the profile. */
export const PayloadProfile = z.object({
  displayName: z.string(),
  email: z.string().nullable(),
  matchCredits: z.number().int(),
}).meta({ id: 'PayloadProfile' });

export const PayloadReferral = z.object({ valid: z.boolean() }).meta({ id: 'PayloadReferral' });

// ── what React asks for ───────────────────────────────────────────────────────────────────────

export const CommandEmail = z.object({ email: z.string() }).meta({ id: 'CommandEmail' });
export const CommandSignUp = z.object({
  email: z.string(),
  displayName: z.string(),
  /** A referral code the screen checked and found valid, or null. */
  referralCode: z.string().nullable(),
  consentAccepted: z.boolean(),
}).meta({ id: 'CommandSignUp' });
/** The code screen: the email's verification after a sign-up, or a sign-in's login code. */
export const CommandVerifyCode = z.object({
  purpose: z.enum(['VERIFY_EMAIL', 'LOGIN']),
  code: z.string(),
}).meta({ id: 'CommandVerifyCode' });
export const CommandReferral = z.object({ code: z.string() }).meta({ id: 'CommandReferral' });
export const CommandNickname = z.object({ displayName: z.string() }).meta({ id: 'CommandNickname' });
export const CommandEmailCode = z.object({ email: z.string(), code: z.string() }).meta({ id: 'CommandEmailCode' });

// ── requests the session sends ────────────────────────────────────────────────────────────────

export const RequestEmail = z.object({ email: z.string() }).meta({ id: 'RequestEmail' });
export const RequestSignUp = z.object({
  email: z.string(),
  displayName: z.string(),
  referralCode: z.string().nullable(),
  consentAccepted: z.boolean(),
}).meta({ id: 'RequestSignUp' });
export const RequestVerifyCode = z.object({
  purpose: z.enum(['VERIFY_EMAIL', 'LOGIN']),
  email: z.string(),
  code: z.string(),
}).meta({ id: 'RequestVerifyCode' });
export const RequestNickname = z.object({ displayName: z.string() }).meta({ id: 'RequestNickname' });
export const RequestEmailCode = z.object({ email: z.string(), code: z.string() }).meta({ id: 'RequestEmailCode' });

// ── the decisions' commands ───────────────────────────────────────────────────────────────────

/** The login code's answer: sent (null), or the server's refusal in its own words. */
export const CommandLoginCodeAnswered = z.object({
  email: z.string(),
  refusal: z.string().nullable(),
}).meta({ id: 'CommandLoginCodeAnswered' });
export const CommandSignedUp = z.object({ email: z.string() }).meta({ id: 'CommandSignedUp' });
export const CommandSignedIn = z.object({ identity: PayloadSignedIn }).meta({ id: 'CommandSignedIn' });
export const CommandProfileLoaded = z.object({ profile: PayloadProfile }).meta({ id: 'CommandProfileLoaded' });
export const CommandEmailChanged = z.object({ email: z.string().nullable() }).meta({ id: 'CommandEmailChanged' });

// ── what the decisions answer the Application ────────────────────────────────────────────────

/** "No account for this address": the email screen turns into the sign-up form in place. */
export const DtoLoginCode = z.object({ signUp: z.boolean() }).meta({ id: 'DtoLoginCode' });

export type IPayloadSignedIn = z.infer<typeof PayloadSignedIn>;
export type IPayloadProfile = z.infer<typeof PayloadProfile>;
export type IPayloadReferral = z.infer<typeof PayloadReferral>;
export type ICommandEmail = z.infer<typeof CommandEmail>;
export type ICommandSignUp = z.infer<typeof CommandSignUp>;
export type ICommandVerifyCode = z.infer<typeof CommandVerifyCode>;
export type ICommandReferral = z.infer<typeof CommandReferral>;
export type ICommandNickname = z.infer<typeof CommandNickname>;
export type ICommandEmailCode = z.infer<typeof CommandEmailCode>;
export type IPayloadEmail = z.infer<typeof RequestEmail>;
export type IPayloadSignUp = z.infer<typeof RequestSignUp>;
export type IPayloadVerifyCode = z.infer<typeof RequestVerifyCode>;
export type IPayloadNickname = z.infer<typeof RequestNickname>;
export type IPayloadEmailCode = z.infer<typeof RequestEmailCode>;
export type ICommandLoginCodeAnswered = z.infer<typeof CommandLoginCodeAnswered>;
export type ICommandSignedUp = z.infer<typeof CommandSignedUp>;
export type ICommandSignedIn = z.infer<typeof CommandSignedIn>;
export type ICommandProfileLoaded = z.infer<typeof CommandProfileLoaded>;
export type ICommandEmailChanged = z.infer<typeof CommandEmailChanged>;
export type IEntityLoginCode = z.infer<typeof DtoLoginCode>;
